import React, { useState, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize, RotateCcw, Users, CreditCard, FileText, Calendar, Receipt, TrendingUp, Database, MoreVertical } from 'lucide-react';

interface Entity {
  id: string;
  name: string;
  icon: any;
  color: string;
  iconColor: string;
  x: number;
  y: number;
  fields: Array<{
    name: string;
    type: string;
    isPrimary?: boolean;
    isForeign?: boolean;
    references?: string;
    required?: boolean;
  }>;
}

interface Relationship {
  from: string;
  to: string;
  label: string;
  cardinality: string;
}

interface ERDiagramProps {
  entities: Entity[];
  relationships: Relationship[];
  isFullscreen?: boolean;
  onFullscreen?: () => void;
  onExitFullscreen?: () => void;
}

const ERDiagram: React.FC<ERDiagramProps> = ({
  entities,
  relationships,
  isFullscreen = false,
  onFullscreen,
  onExitFullscreen
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoveredEntity, setHoveredEntity] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const minZoom = 0.3;
  const maxZoom = 3;
  const zoomStep = 0.2;

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + zoomStep, maxZoom));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - zoomStep, minZoom));
  };

  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleEntityHover = (entityId: string, e: React.MouseEvent) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setHoveredEntity(entityId);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const tooltipWidth = 320; 
      const tooltipHeight = 200;

      let adjustedX = x + 10;
      let adjustedY = y - 10;

      if (adjustedX + tooltipWidth > rect.width) {
        adjustedX = x - tooltipWidth - 10;
      }

      if (adjustedY + tooltipHeight > rect.height) {
        adjustedY = y - tooltipHeight - 10;
      }

      if (adjustedY < 0) {
        adjustedY = 10;
      }

      if (adjustedX < 0) {
        adjustedX = 10;
      }

      setTooltipPosition({
        x: adjustedX,
        y: adjustedY
      });

      hoverTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 300);
    }
  };

  const handleEntityLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setHoveredEntity(null);
        setShowTooltip(false);
      }
    }, 100);
  };

  const handleTooltipEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsTooltipHovered(true);
  };

  const handleTooltipLeave = () => {
    setIsTooltipHovered(false);
    setHoveredEntity(null);
    setShowTooltip(false);
  };

  const getEntityPosition = (entity: Entity) => ({
    x: entity.x + pan.x,
    y: entity.y + pan.y
  });

  const getRelationshipPath = (rel: Relationship) => {
    const fromEntity = entities.find(e => e.id === rel.from);
    const toEntity = entities.find(e => e.id === rel.to);

    if (!fromEntity || !toEntity) return '';

    const fromPos = getEntityPosition(fromEntity);
    const toPos = getEntityPosition(toEntity);

    const fromX = fromPos.x + 140;
    const fromY = fromPos.y + 16; 
    const toX = toPos.x + 140;
    const toY = toPos.y + 16;

    return `M ${fromX} ${fromY} L ${toX} ${toY}`;
  };

  const getRelationshipLabelPosition = (rel: Relationship) => {
    const fromEntity = entities.find(e => e.id === rel.from);
    const toEntity = entities.find(e => e.id === rel.to);

    if (!fromEntity || !toEntity) return { x: 0, y: 0 };

    const fromPos = getEntityPosition(fromEntity);
    const toPos = getEntityPosition(toEntity);

    return {
      x: (fromPos.x + toPos.x + 400) / 2,
      y: (fromPos.y + toPos.y + 100) / 2
    };
  };

  const renderEntity = (entity: Entity) => {
    const Icon = entity.icon;
    const position = getEntityPosition(entity);
    const scaledWidth = 280 * zoom;
    const scaledHeight = Math.max(120, entity.fields.length * 20 + 60) * zoom;

    return (
      <g key={entity.id} transform={`scale(${zoom})`}>
        {/* Entity Rectangle */}
        <rect
          x={position.x / zoom}
          y={position.y / zoom}
          width={280}
          height={Math.max(120, entity.fields.length * 20 + 60)}
          rx="6"
          fill="white"
          stroke="#e5e7eb"
          strokeWidth="1"
          className="drop-shadow-sm"
        />

        {/* Entity Header */}
        <rect
          x={position.x / zoom}
          y={position.y / zoom}
          width={280}
          height={32}
          rx="6"
          fill="#f8fafc"
        />

        {/* Entity Name */}
        <foreignObject x={position.x / zoom + 12} y={position.y / zoom + 8} width={240} height={20} style={{ userSelect: 'none' }}>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-900 text-sm">{entity.name}</span>
            <div className="flex items-center space-x-1">
              <button
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                onMouseEnter={(e) => handleEntityHover(entity.id, e)}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              <button className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </foreignObject>

        {/* Entity Fields */}
        <foreignObject x={position.x / zoom + 12} y={position.y / zoom + 40} width={256} height={entity.fields.length * 20} style={{ userSelect: 'none' }}>
          <div className="space-y-1">
            {entity.fields.map((field, index) => (
              <div key={index} className="flex items-center justify-between text-xs py-1">
                <div className="flex items-center space-x-2">
                  {field.isPrimary && (
                    <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  )}
                  {field.isForeign && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                  {!field.isPrimary && !field.isForeign && (
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  <span className="font-medium text-gray-700">{field.name}</span>
                </div>
                <span className="text-gray-500 font-mono text-xs">{field.type}</span>
              </div>
            ))}
          </div>
        </foreignObject>
      </g>
    );
  };

  const renderRelationship = (rel: Relationship) => {
    const path = getRelationshipPath(rel);
    const labelPos = getRelationshipLabelPosition(rel);

    return (
      <g key={`${rel.from}-${rel.to}`}>
        {/* Relationship Line */}
        <path
          d={path}
          stroke="#6b7280"
          strokeWidth="1.5"
          fill="none"
          strokeDasharray="5,5"
          markerEnd="url(#arrowhead)"
        />
      </g>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`relative bg-gray-50 border border-gray-200 rounded-lg overflow-hidden ${isFullscreen ? 'fixed inset-0 z-20' : 'h-full'
        }`}
      style={{ userSelect: 'none' }}
    >
      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex space-x-2">
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          disabled={zoom <= minZoom}
        >
          <ZoomOut className="w-4 h-4 text-gray-600" />
        </button>
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          disabled={zoom >= maxZoom}
        >
          <ZoomIn className="w-4 h-4 text-gray-600" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        >
          <RotateCcw className="w-4 h-4 text-gray-600" />
        </button>
        {onFullscreen && (
          <button
            onClick={isFullscreen ? onExitFullscreen : onFullscreen}
            className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <Maximize className="w-4 h-4 text-gray-600" />
          </button>
        )}
      </div>

      {/* Zoom Level Indicator */}
      <div className="absolute top-4 left-4 z-10">
        <div className="px-3 py-1 bg-white border border-gray-300 rounded-lg shadow-sm">
          <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      {/* SVG Diagram */}
      <div className="w-full h-full overflow-hidden">
        <svg
          ref={svgRef}
          className="cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            minWidth: '1000px',
            minHeight: '800px',
            width: '100%',
            height: '100%',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none'
          }}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#6b7280"
              />
            </marker>
          </defs>

          {/* Relationships */}
          {relationships.map(renderRelationship)}

          {/* Entities */}
          {entities.map(renderEntity)}
        </svg>

        {/* Tooltip for showing all fields */}
        {hoveredEntity && showTooltip && (
          <div
            className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 max-w-sm animate-in fade-in-0 zoom-in-95 duration-200"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              pointerEvents: 'auto'
            }}
            onMouseEnter={handleTooltipEnter}
            onMouseLeave={handleTooltipLeave}
          >
            {(() => {
              const entity = entities.find(e => e.id === hoveredEntity);
              if (!entity) return null;

              return (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm">{entity.name} - All Fields</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-hide">
                    {entity.fields.map((field, index) => (
                      <div key={index} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center space-x-2">
                          {field.isPrimary && (
                            <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          )}
                          {field.isForeign && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                          {!field.isPrimary && !field.isForeign && (
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                          <span className="font-medium text-gray-700">{field.name}</span>
                        </div>
                        <span className="text-gray-500 font-mono text-xs">{field.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ERDiagram;
